import {
  Alert,
  AlertDescription,
  AlertIcon,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { ListCard } from '@duke-hq/ui/components/Cards/ListCard';
import { Plural, Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { CourseItemStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseItemStatus.schema';
import { skipToken } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Settings } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { AddUnitsModal } from '../AddUnitsModal';
import { useCourseEditorState } from '../CourseEditorState';
import { CourseSettings } from '../CourseSettings';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { UnitList } from './UnitList';
import { UnitsCoverageSummary } from './UnitsCoverageSummary';
import { testIds } from './testIds';
import { withPermission } from '../../withPermission';
import { CourseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import { EditorLayout } from '../../navbar/EditorLayout';
import { EditorStatus } from '../../navbar/EditorStatus';
import { EditorBreadcrumbs } from '../../navbar/EditorBreadcrumbs';
import { CoursePublishButton } from '../../navbar/CoursePublishButton';
import { CourseTranslationsToolbarPill } from '../../components/translations/CourseTranslationsToolbarPill';

export const CourseEditor = withPermission('canManageCourseContent', () => {
  const { courseId } = useParams();
  const {
    setIsCourseEditorOpened,
    isCourseSettingsOpened,
    courseSettingsButtonProps,
    closeCourseSettings,
    isCourseSettingsExpanded,
  } = useCourseEditorState();
  if (courseId === null || courseId === undefined) {
    throw new Error('courseId is required');
  }

  useEffect(() => {
    setIsCourseEditorOpened(true);
    return () => {
      setIsCourseEditorOpened(false);
    };
  }, [setIsCourseEditorOpened]);

  const [course, courseFetchQuery] = trpcAdmin.courses.byId.useSuspenseQuery(courseId);

  const isSuperseded =
    course?.status === CourseStatusSchema.enum.SUPERSEDED ||
    course?.status === CourseStatusSchema.enum.PENDING_SUPERSESSION;

  const { data: supersessionHistory } = trpcAdmin.courses.courseSupersessionHistory.useQuery(
    isSuperseded ? { courseId } : skipToken,
  );

  if (courseFetchQuery.isLoading) {
    return (
      <Flex flexDirection="column" alignItems="center" justifyContent="center" h="100%">
        <Spinner />
      </Flex>
    );
  }

  if (!course) {
    return <Text>{t`Course not found`}</Text>;
  }

  const scheduledUnitIds = new Set(
    course.courseSchedule
      .map((s) => s.unitOfCompetencyId)
      .filter((id): id is string => id !== null),
  );

  const hasSupersededInSchedule = course.courseItems.some(
    (item) =>
      item.status === CourseItemStatusSchema.enum.SUPERSEDED &&
      item.unit_of_competency_id !== null &&
      scheduledUnitIds.has(item.unit_of_competency_id),
  );

  const unscheduledItems = course.courseItems.filter(
    (item) =>
      item.status === CourseItemStatusSchema.enum.ACTIVE &&
      item.unit_of_competency_id !== null &&
      !scheduledUnitIds.has(item.unit_of_competency_id),
  );

  return (
    <EditorLayout
      toolbar={{
        breadcrumbs: (
          <EditorBreadcrumbs
            crumbs={[{ label: t`Courses`, to: '/courses' }]}
            current={
              <HStack spacing="2">
                {course.identifier ? (
                  <Text fontSize="sm" fontWeight="medium" color="fg.muted" whiteSpace="nowrap">
                    {course.identifier}
                  </Text>
                ) : null}
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  whiteSpace="nowrap"
                  data-testid={testIds.title}
                >
                  {course.title}
                </Text>
              </HStack>
            }
          />
        ),
        status: (
          <EditorStatus
            published={{
              isPublished: course.published_at != null,
              publishedAt: course.published_at,
            }}
            superseded={
              isSuperseded
                ? {
                    label: <Trans>Superseded</Trans>,
                    tooltip: supersessionHistory?.supersededByCourse
                      ? t`Superseded by ${supersessionHistory.supersededByCourse.identifier ?? ''} ${supersessionHistory.supersededByCourse.title}`.trim()
                      : undefined,
                  }
                : undefined
            }
          />
        ),
        actions: [
          {
            id: 'translations',
            node: <CourseTranslationsToolbarPill courseId={course.id} />,
          },
          {
            id: 'settings',
            node: (
              <Tooltip label={t`Settings`}>
                <IconButton
                  {...courseSettingsButtonProps()}
                  variant="ghost"
                  size="sm"
                  aria-label={t`Settings`}
                >
                  <Settings size={iconSize.md} color="currentColor" />
                </IconButton>
              </Tooltip>
            ),
          },
          {
            id: 'publish',
            node: <CoursePublishButton courseId={course.id} />,
            startsGroup: true,
          },
        ],
      }}
      drawer={{
        isOpen: isCourseSettingsOpened,
        onClose: closeCourseSettings,
        title: <Trans>Settings</Trans>,
        body: <CourseSettings />,
        size: isCourseSettingsExpanded ? 'xl' : 'md',
        closeOnOverlayClick: true,
      }}
      body={{
        kind: 'content',
        children: (
          <>
            {/* The prominent title heading is kept on the page (not only in the
                toolbar breadcrumb). It's display-only here: the course title is
                edited in the Settings drawer, so both this and the breadcrumb just
                reflect the same course data. */}
            <Flex gap={4}>
              <Text textStyle="3xl" fontWeight="medium" color="fg.muted">
                {course.identifier}
              </Text>
              <Text textStyle="3xl" fontWeight="medium">
                {course.title}
              </Text>
            </Flex>

            {hasSupersededInSchedule ? (
              <Alert status="error" borderWidth="1px" borderRadius="md" borderColor="danger">
                <AlertIcon />
                <AlertDescription fontWeight="500">
                  <Trans>
                    This course contains superseded units. Update the student schedule to remove or
                    replace them.
                  </Trans>
                </AlertDescription>
              </Alert>
            ) : null}

            {unscheduledItems.length > 0 ? (
              <Alert
                status="info"
                borderWidth="1px"
                borderRadius="md"
                borderColor="info"
                alignItems="start"
              >
                <AlertIcon mt={0.5} />
                <AlertDescription>
                  <Trans>
                    The following{' '}
                    <Plural value={unscheduledItems.length} one="unit has" other="units have" /> not
                    been added to the course schedule yet:
                  </Trans>
                  <Stack mt={1} gap={1}>
                    {unscheduledItems.map((item) => (
                      <Text key={item.id} fontSize="sm" fontWeight="500">
                        {item.unitOfCompetency?.code} — {item.unitOfCompetency?.title}
                      </Text>
                    ))}
                  </Stack>
                </AlertDescription>
              </Alert>
            ) : null}

            <ListCard
              cardTitle={t`Units of Competency`}
              cardSubtitle={
                course.courseItems.length > 0
                  ? t`Units added to this course.`
                  : t`No units in this course.`
              }
              cardPrimaryAction={
                <HStack spacing={3}>
                  <UnitsCoverageSummary courseId={course.id} />
                  <AddUnitsModal courseId={course.id} currentCourseItems={course.courseItems} />
                </HStack>
              }
              cardContent={
                course.courseItems.length > 0 ? (
                  <UnitList
                    courseId={course.id}
                    courseItems={course.courseItems}
                    scheduledUnitIds={scheduledUnitIds}
                  />
                ) : null
              }
            />
          </>
        ),
      }}
    />
  );
});
